import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  AppBar,
  Toolbar,
  Avatar,
  IconButton,
  useTheme,
  alpha,
  Fade,
  Zoom,
  Slide,
  LinearProgress,
  Divider,
  Badge
} from '@mui/material';
import {
  TwoWheeler as MotorcycleIcon,
  LocalShipping,
  CheckCircle,
  AccessTime,
  Phone,
  LocationOn as MapIcon,
  Search,
  Logout,
  Person,
  ShoppingCart,
  Speed,
  TrendingUp,
  Star,
  NotificationsActive,
  Map,
  DirectionsBike,
  DeliveryDining,
  AssignmentTurnedIn,
  PendingActions,
  AccountBalanceWallet,
  Payments,
  LocalOffer,
  Close as CloseIcon
} from '@mui/icons-material';
import { api } from '../services/AuthService';
import { useAuth } from '../services/AuthService';
import { formatCep, lookupCep } from '../utils/cep';
import WebPushButton from './WebPushButton';

function MotoboyDashboard() {
  const theme = useTheme();
  const [pedidosDisponiveis, setPedidosDisponiveis] = useState([]);
  const [meusPedidos, setMeusPedidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dayFilter, setDayFilter] = useState('todos'); // 'todos', 'hoje', 'ontem'
  const [stats, setStats] = useState({
    entregasHoje: 0,
    entregasMes: 0,
    pedidosPendentes: 0,
    totalGanho: 0,
    avaliacaoMedia: 4.8
  });
  const [buscaId, setBuscaId] = useState('');
  const [pedidoBuscado, setPedidoBuscado] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [buscando, setBuscando] = useState(false);
  const [animateStats, setAnimateStats] = useState(false);
  const [payoutSummary, setPayoutSummary] = useState(null);
  const [payoutLoadError, setPayoutLoadError] = useState(false);
  const [pixDialogOpen, setPixDialogOpen] = useState(false);
  const [pixKeyValue, setPixKeyValue] = useState('');
  const [pixKeyType, setPixKeyType] = useState('CPF');
  const [payoutBusy, setPayoutBusy] = useState(false);
  const [serviceCity, setServiceCity] = useState('');
  const [serviceCep, setServiceCep] = useState('');
  const [citySaving, setCitySaving] = useState(false);
  const dataRequestInFlight = useRef(false);
  const { user, logout } = useAuth();

  const isAppCourier = Boolean(user && !user.lanchonete_id && String(user.motoboy_scope || '').toUpperCase() !== 'LOJA');

  const refreshPayoutSummary = useCallback(async () => {
    if (!isAppCourier) {
      setPayoutSummary(null);
      return;
    }
    try {
      const response = await api.get('/motoboys/payouts/me');
      setPayoutSummary(response.data || null);
      setPayoutLoadError(false);
    } catch (error) {
      setPayoutSummary(null);
      setPayoutLoadError(true);
      if (error.response?.status !== 400) console.error('Erro ao carregar saldo de saques:', error);
    }
  }, [isAppCourier]);

  useEffect(() => {
    setServiceCity(user?.service_city || '');
    setServiceCep(user?.service_cep || '');
  }, [user?.service_city, user?.service_cep]);

  const filterPedidosByDay = (pedidos, filter) => {
    if (filter === 'todos') return pedidos;
    
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    return pedidos.filter(pedido => {
      const pedidoDate = new Date(pedido.created_at);
      
      if (filter === 'hoje') {
        return pedidoDate.toDateString() === today.toDateString();
      } else if (filter === 'ontem') {
        return pedidoDate.toDateString() === yesterday.toDateString();
      }
      
      return true;
    });
  };

  const loadData = useCallback(async ({ quiet = false } = {}) => {
    if (dataRequestInFlight.current) return;
    dataRequestInFlight.current = true;
    try {
      if (!quiet) setLoading(true);
      
      // Carregar pedidos disponíveis para entrega
      const [disponiveisResult, chamadasResult] = await Promise.allSettled([
        api.get('/pedidos/available/delivery'), api.get('/delivery-calls/available')
      ]);
      const pedidosNormais = disponiveisResult.status === 'fulfilled'
        ? (disponiveisResult.value.data || [])
        : [];
      const chamadasAvulsas = chamadasResult.status === 'fulfilled'
        ? (chamadasResult.value.data || [])
        : [];
      if (disponiveisResult.status === 'rejected') {
        console.error('Erro ao carregar entregas normais:', disponiveisResult.reason);
      }
      if (chamadasResult.status === 'rejected') {
        console.error('Erro ao carregar chamados avulsos:', chamadasResult.reason);
      }
      // As duas rotas ja devolvem somente as corridas autorizadas para o
      // motoboy logado. Nao refiltrar usando o usuario salvo no navegador,
      // pois ele pode estar desatualizado depois de uma mudanca de escopo.
      const disponiveisData = [...pedidosNormais, ...chamadasAvulsas];
      const disponiveisFiltered = filterPedidosByDay(disponiveisData, dayFilter);
      setPedidosDisponiveis(disponiveisFiltered);
      
      // Carregar meus pedidos
      const [meusPedidosResult, minhasChamadasResult] = await Promise.allSettled([
        api.get('/pedidos/my-deliveries'), api.get('/delivery-calls/mine')
      ]);
      const meusPedidosData = [
        ...(meusPedidosResult.status === 'fulfilled' ? (meusPedidosResult.value.data || []) : []),
        ...(minhasChamadasResult.status === 'fulfilled' ? (minhasChamadasResult.value.data || []) : [])
      ];
      if (meusPedidosResult.status === 'rejected') {
        console.error('Erro ao carregar entregas do motoboy:', meusPedidosResult.reason);
      }
      if (minhasChamadasResult.status === 'rejected') {
        console.error('Erro ao carregar chamados aceitos:', minhasChamadasResult.reason);
      }
      const meusPedidosFiltered = filterPedidosByDay(meusPedidosData, dayFilter);
      setMeusPedidos(meusPedidosFiltered);
      
      // Carregar estatísticas
      await refreshPayoutSummary();
      const statsResponse = await api.get('/motoboys/stats');
      setStats(statsResponse.data || {
        entregasHoje: 0,
        entregasMes: 0,
        pedidosPendentes: 0
      });
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      dataRequestInFlight.current = false;
      if (!quiet) setLoading(false);
    }
  }, [dayFilter, refreshPayoutSummary]);

  useEffect(() => {
    loadData();
    // Ativar animações após carregar
    setTimeout(() => setAnimateStats(true), 500);
    const refreshTimer = setInterval(() => loadData({ quiet: true }), 10000);
    return () => clearInterval(refreshTimer);
  }, [dayFilter, loadData]);

  const handleAceitarPedido = async (pedido) => {
    try {
      const item = typeof pedido === 'object' ? pedido : { id: pedido };
      await api.post(item.entity_type === 'delivery_call' ? `/delivery-calls/${item.id}/accept` : `/pedidos/${item.id}/accept`);
      
      // Recarregar dados
      await loadData();
      
      alert('Pedido aceito com sucesso!');
    } catch (error) {
      alert('Erro ao aceitar pedido: ' + (error.response?.data?.error || 'Tente novamente'));
    }
  };

  const handleStatusEntrega = async (pedido, status) => {
    try {
      const item = typeof pedido === 'object' ? pedido : { id: pedido };
      if (item.entity_type === 'delivery_call') await api.put(`/delivery-calls/${item.id}/complete`);
      else await api.patch(`/pedidos/${item.id}/status`, { status });
      
      // Recarregar dados
      await loadData();
      
      alert('Status atualizado com sucesso!');
    } catch (error) {
      alert('Erro ao atualizar status: ' + (error.response?.data?.error || 'Tente novamente'));
    }
  };

  const handleBuscarPedido = async () => {
    if (!buscaId.trim()) {
      alert('Digite o código do pedido');
      return;
    }

    try {
      setBuscando(true);
      const response = await api.get(`/pedidos/${buscaId.trim()}`);
      setPedidoBuscado(response.data);
      setDialogOpen(true);
    } catch (error) {
      if (error.response?.status === 404) {
        alert('Pedido não encontrado');
      } else {
        alert('Erro ao buscar pedido: ' + (error.response?.data?.error || 'Tente novamente'));
      }
      setPedidoBuscado(null);
    } finally {
      setBuscando(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleBuscarPedido();
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setPedidoBuscado(null);
    setBuscaId('');
  };

  const handleSavePixKey = async () => {
    try {
      setPayoutBusy(true);
      const response = await api.put('/motoboys/payouts/pix-key', { key: pixKeyValue, type: pixKeyType });
      setPixKeyValue('');
      setPixDialogOpen(false);
      window.alert(`Chave Pix ${response.data?.pix_key_masked || ''} cadastrada com proteção.`);
      await refreshPayoutSummary();
    } catch (error) {
      window.alert(error.response?.data?.error || 'Não foi possível salvar a chave Pix.');
    } finally {
      setPayoutBusy(false);
    }
  };

  const handleRequestPayout = async () => {
    if (!payoutSummary) {
      window.alert('Não consegui carregar seu saldo. Tente atualizar a página e, se continuar, avise o Admin Master.');
      await refreshPayoutSummary();
      return;
    }
    if (!payoutSummary.pix_key_setup_ready) {
      window.alert('O cadastro seguro da chave Pix está temporariamente indisponível. Avise o Admin Master para habilitá-lo.');
      return;
    }
    if (!payoutSummary?.has_pix_key) {
      setPixDialogOpen(true);
      return;
    }
    const amount = Number(payoutSummary.eligible_amount || 0);
    const deliveries = Number(payoutSummary.eligible_delivery_count || 0);
    if (!deliveries) {
      window.alert(payoutSummary.has_open_request
        ? 'Você já tem uma solicitação de saque aguardando pagamento.'
        : 'Você ainda não tem entregas concluídas disponíveis para saque.');
      return;
    }
    if (!window.confirm(`Solicitar o saque de ${deliveries} entrega(s), no valor de R$ ${amount.toFixed(2)}? O saldo ficará reservado até o Admin Master confirmar o pagamento. A previsão informada para pagamento é às 00:00; o crédito depende da confirmação do Mercado Pago e do banco de destino.`)) return;
    try {
      setPayoutBusy(true);
      await api.post('/motoboys/payouts/request');
      window.alert(payoutSummary.payouts_enabled
        ? 'Solicitação enviada. O Admin Master poderá acompanhar e processar o pagamento, com previsão para 00:00. O crédito depende da confirmação do Mercado Pago e do banco de destino.'
        : 'Solicitação enviada. O Admin Master fará o Pix pelo Mercado Pago e confirmará no painel. A previsão informada para pagamento é às 00:00; o crédito depende da confirmação do Mercado Pago e do banco de destino.');
      await refreshPayoutSummary();
    } catch (error) {
      window.alert(error.response?.data?.error || 'Não foi possível solicitar o saque.');
    } finally {
      setPayoutBusy(false);
    }
  };

  const handleSaveServiceCity = async () => {
    const city = serviceCity.trim();
    const cep = serviceCep.replace(/\D/g, '');
    if (cep.length !== 8 || !city) {
      window.alert('Informe um CEP válido para identificar sua cidade de atendimento.');
      return;
    }
    try {
      setCitySaving(true);
      await api.put('/users/profile', { service_cep: cep, service_city: city });
      setServiceCity(city);
      window.alert('Cidade de atendimento atualizada. As próximas corridas seguirão essa cidade.');
      await loadData({ quiet: true });
    } catch (error) {
      window.alert(error.response?.data?.error || 'Não foi possível atualizar sua cidade.');
    } finally {
      setCitySaving(false);
    }
  };

  const handleServiceCepBlur = async () => {
    if (serviceCep.replace(/\D/g, '').length !== 8) return;
    try {
      const location = await lookupCep(serviceCep);
      setServiceCep(location.cep);
      setServiceCity(location.city);
    } catch (error) {
      setServiceCity('');
      window.alert(error.message || 'CEP não encontrado.');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pendente': return 'warning';
      case 'aceito': return 'info';
      case 'preparando': return 'info';
      case 'saiu_entrega': return 'primary';
      case 'entregue': return 'success';
      case 'cancelado': return 'error';
      default: return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pendente': return 'Pendente';
      case 'aceito': return 'Aceito';
      case 'preparando': return 'Preparando';
      case 'saiu_entrega': return 'Saiu para Entrega';
      case 'entregue': return 'Entregue';
      case 'cancelado': return 'Cancelado';
      default: return status;
    }
  };

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        sx={{ 
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          flexDirection: 'column',
          gap: 3
        }}
      >
        <Fade in={true}>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress 
              size={80} 
              thickness={4}
              sx={{ 
                color: theme.palette.primary.contrastText,
                mb: 2
              }} 
            />
            <Typography variant="h6" color="inherit">
              Carregando dashboard...
            </Typography>
            <Typography variant="body2" color="inherit" sx={{ opacity: 0.8 }}>
              Preparando suas entregas
            </Typography>
          </Box>
        </Fade>
        <LinearProgress 
          sx={{ 
            width: '200px',
            bgcolor: alpha(theme.palette.primary.contrastText, 0.2),
            '& .MuiLinearProgress-bar': {
              bgcolor: theme.palette.primary.contrastText
            }
          }} 
        />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.background.default }}>
      {/* Header Personalizado */}
      <AppBar 
        position="sticky" 
        elevation={0}
        sx={{ 
          bgcolor: theme.palette.primary.main,
          borderBottom: `1px solid ${alpha(theme.palette.primary.contrastText, 0.12)}`
        }}
      >
        <Toolbar sx={{ py: 1 }}>
          <Box display="flex" alignItems="center" flexGrow={1}>
            <Avatar 
              sx={{ 
                bgcolor: alpha(theme.palette.primary.contrastText, 0.15),
                color: theme.palette.primary.contrastText,
                mr: 2
              }}
            >
              <Person />
            </Avatar>
            <Box>
              <Typography variant="h6" color="inherit" fontWeight="bold">
                Painel do Motoboy
              </Typography>
              <Typography variant="body2" color="inherit" sx={{ opacity: 0.8 }}>
                {user?.name || 'Motoboy'}
              </Typography>
            </Box>
          </Box>
          <WebPushButton user={user} />
          <IconButton 
            color="inherit" 
            onClick={logout}
            sx={{ 
              '&:hover': { 
                bgcolor: alpha(theme.palette.primary.contrastText, 0.1) 
              } 
            }}
          >
            <Logout />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 3 }}>
        {/* Campo de busca de pedidos */}
        <Card 
          sx={{ 
            mb: 3,
            background: `linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" mb={2}>
              <Avatar sx={{ bgcolor: theme.palette.primary.main, mr: 2 }}>
                <Search />
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight="bold" color="primary">
                  Buscar Pedido por Código
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Encontre pedidos rapidamente pelo ID
                </Typography>
              </Box>
            </Box>
            <Box display="flex" gap={2}>
              <TextField
                fullWidth
                placeholder="Digite o código do pedido (ex: 123)"
                value={buscaId}
                onChange={(e) => setBuscaId(e.target.value)}
                onKeyPress={handleKeyPress}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#ffffff',
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main,
                      borderWidth: 2,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main,
                      borderWidth: 2,
                      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
                    },
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: theme.palette.primary.main }} />
                    </InputAdornment>
                  ),
                }}
                disabled={buscando}
              />
              <Button
                variant="contained"
                onClick={handleBuscarPedido}
                disabled={buscando || !buscaId.trim()}
                sx={{ 
                  minWidth: 120,
                  borderRadius: 2,
                  px: 3,
                  py: 1.5,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  '&:hover': { 
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                  },
                  '&:disabled': { 
                    background: alpha(theme.palette.action.disabled, 0.5),
                    color: theme.palette.action.disabled
                  }
                }}
              >
                {buscando ? 'Buscando...' : 'Buscar'}
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Estatísticas Modernas */}
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} md={3}>
            <Zoom in={animateStats} style={{ transitionDelay: animateStats ? '100ms' : '0ms' }}>
              <Card 
                sx={{ 
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  color: 'white',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': { 
                    transform: 'translateY(-8px) scale(1.02)',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.2)'
                  },
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                <Box sx={{ 
                  position: 'absolute',
                  top: -20,
                  right: -20,
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  bgcolor: alpha('#fff', 0.1)
                }} />
                <CardContent sx={{ textAlign: 'center', py: 3, position: 'relative', zIndex: 1 }}>
                  <Box
                    sx={{
                      width: 70,
                      height: 70,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: alpha('#fff', 0.2),
                      mx: 'auto',
                      mb: 2
                    }}
                  >
                    <LocalShipping sx={{ fontSize: 35, color: 'white' }} />
                  </Box>
                  <Typography variant="h4" fontWeight="bold" sx={{ mb: 1 }}>
                    {stats.entregasHoje}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Entregas Hoje
                  </Typography>
                  <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
                    <TrendingUp sx={{ fontSize: 16, mr: 0.5 }} />
                    <Typography variant="caption">
                      +12% vs ontem
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Zoom>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Zoom in={animateStats} style={{ transitionDelay: animateStats ? '200ms' : '0ms' }}>
              <Card 
                sx={{ 
                  background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
                  color: 'white',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': { 
                    transform: 'translateY(-8px) scale(1.02)',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.2)'
                  },
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                <Box sx={{ 
                  position: 'absolute',
                  top: -20,
                  right: -20,
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  bgcolor: alpha('#fff', 0.1)
                }} />
                <CardContent sx={{ textAlign: 'center', py: 3, position: 'relative', zIndex: 1 }}>
                  <Box
                    sx={{
                      width: 70,
                      height: 70,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: alpha('#fff', 0.2),
                      mx: 'auto',
                      mb: 2
                    }}
                  >
                    <CheckCircle sx={{ fontSize: 35, color: 'white' }} />
                  </Box>
                  <Typography variant="h4" fontWeight="bold" sx={{ mb: 1 }}>
                    {stats.entregasMes}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Entregas no Mês
                  </Typography>
                  <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
                    <TrendingUp sx={{ fontSize: 16, mr: 0.5 }} />
                    <Typography variant="caption">
                      +25% vs mês passado
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Zoom>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Zoom in={animateStats} style={{ transitionDelay: animateStats ? '300ms' : '0ms' }}>
              <Card 
                sx={{ 
                  background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`,
                  color: 'white',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': { 
                    transform: 'translateY(-8px) scale(1.02)',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.2)'
                  },
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                <Box sx={{ 
                  position: 'absolute',
                  top: -20,
                  right: -20,
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  bgcolor: alpha('#fff', 0.1)
                }} />
                <CardContent sx={{ textAlign: 'center', py: 3, position: 'relative', zIndex: 1 }}>
                  <Box
                    sx={{
                      width: 70,
                      height: 70,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: alpha('#fff', 0.2),
                      mx: 'auto',
                      mb: 2
                    }}
                  >
                    <AccessTime sx={{ fontSize: 35, color: 'white' }} />
                  </Box>
                  <Typography variant="h4" fontWeight="bold" sx={{ mb: 1 }}>
                    {stats.pedidosPendentes}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Pedidos Pendentes
                  </Typography>
                  <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
                    <PendingActions sx={{ fontSize: 16, mr: 0.5 }} />
                    <Typography variant="caption">
                      Aguardando ação
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Zoom>
          </Grid>

          <Grid item xs={12} md={3}>
            <Zoom in={animateStats} style={{ transitionDelay: animateStats ? '400ms' : '0ms' }}>
              <Card 
                sx={{ 
                  background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
                  color: 'white',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': { 
                    transform: 'translateY(-8px) scale(1.02)',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.2)'
                  },
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                <Box sx={{ 
                  position: 'absolute',
                  top: -20,
                  right: -20,
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  bgcolor: alpha('#fff', 0.1)
                }} />
                <CardContent sx={{ textAlign: 'center', py: 3, position: 'relative', zIndex: 1 }}>
                  <Box
                    sx={{
                      width: 70,
                      height: 70,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: alpha('#fff', 0.2),
                      mx: 'auto',
                      mb: 2
                    }}
                  >
                    <Speed sx={{ fontSize: 35, color: 'white' }} />
                  </Box>
                  <Typography variant="h4" fontWeight="bold" sx={{ mb: 1 }}>
                    {stats.avaliacaoMedia}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Avaliação Média
                  </Typography>
                  <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
                    <Star sx={{ fontSize: 16, mr: 0.5 }} />
                    <Typography variant="caption">
                      Excelente!
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Zoom>
          </Grid>
        </Grid>

        {isAppCourier && (
          <Card sx={{ mb: 3, borderRadius: 3 }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>CEP onde faço entregas</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Digite seu CEP para identificar a cidade e receber corridas das lanchonetes da região.
              </Typography>
              <Box display="flex" gap={1} flexWrap="wrap">
                <TextField label="CEP de atendimento" value={formatCep(serviceCep)} onChange={(event) => { setServiceCep(formatCep(event.target.value)); setServiceCity(''); }} onBlur={handleServiceCepBlur} inputProps={{ maxLength: 9, inputMode: 'numeric' }} sx={{ flex: '1 1 250px' }} />
                <Typography sx={{ alignSelf: 'center' }}>Cidade identificada: {serviceCity || 'informe o CEP'}</Typography>
                <Button variant="contained" onClick={handleSaveServiceCity} disabled={citySaving || serviceCep.replace(/\D/g, '').length !== 8 || !serviceCity.trim()}>{citySaving ? 'Salvando...' : 'Salvar CEP'}</Button>
              </Box>
            </CardContent>
          </Card>
        )}

        {isAppCourier && (
          <Card sx={{ mb: 4, border: `1px solid ${alpha(theme.palette.success.main, 0.25)}`, borderRadius: 3 }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} flexWrap="wrap">
                <Box display="flex" alignItems="center" gap={2}>
                  <Avatar sx={{ bgcolor: alpha(theme.palette.success.main, 0.12), color: 'success.dark' }}><AccountBalanceWallet /></Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">Entregas a receber</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {Number(payoutSummary?.outstanding_delivery_count || 0)} entrega(s) pendentes · R$ {Number(payoutSummary?.outstanding_amount || 0).toFixed(2)}
                    </Typography>
                  </Box>
                </Box>
                <Box display="flex" gap={1} flexWrap="wrap">
                  <Button variant="outlined" onClick={() => payoutSummary?.pix_key_setup_ready && setPixDialogOpen(true)} disabled={!payoutSummary?.pix_key_setup_ready}>
                    {payoutSummary?.has_pix_key ? `Pix ${payoutSummary.pix_key_masked} · Alterar` : 'Cadastrar chave Pix'}
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<Payments />}
                    onClick={handleRequestPayout}
                    disabled={payoutBusy || payoutSummary?.has_open_request}
                  >
                    {payoutBusy ? 'Enviando...' : payoutSummary?.has_open_request ? 'Aguardando pagamento' : 'Sacar'}
                  </Button>
                </Box>
              </Box>
              {!payoutSummary && <Typography variant="body2" color="error.main" mt={2}>{payoutLoadError ? 'Não foi possível carregar seu saldo. Use “Sacar” para tentar novamente ou atualize a página.' : 'Carregando seu saldo de entregas…'}</Typography>}
              {payoutSummary && !payoutSummary.pix_key_setup_ready && <Typography variant="body2" color="text.secondary" mt={2}>Cadastro de Pix temporariamente indisponível. Avise o Admin Master para habilitar o saque.</Typography>}
              {payoutSummary?.pix_key_setup_ready && !payoutSummary.payouts_enabled && <Typography variant="body2" color="text.secondary" mt={2}>Payouts automático indisponível nesta conta. O Admin Master fará o Pix manualmente pelo Mercado Pago.</Typography>}
              {payoutSummary && !payoutSummary.has_open_request && !Number(payoutSummary.eligible_delivery_count || 0) && <Typography variant="body2" color="text.secondary" mt={2}>Nenhuma entrega concluída disponível para saque agora.</Typography>}
              <Typography variant="body2" color="text.secondary" mt={1}>
                Ao solicitar, as entregas ficam reservadas. O saldo pendente é zerado depois que o Admin Master confirmar o pagamento.
              </Typography>
              {!!payoutSummary.history?.[0] && (
                <Typography variant="body2" color="text.secondary" mt={1}>
                  Última solicitação: {{
                    paid: 'paga', scheduled: 'agendada para 00:00', requested: 'aguardando Admin Master',
                    processing: 'em conferência no Mercado Pago', failed: 'não concluída; saldo disponível novamente',
                    cancelled: 'cancelada'
                  }[payoutSummary.history[0].status] || payoutSummary.history[0].status}
                </Typography>
              )}
            </CardContent>
          </Card>
        )}

      {/* Pedidos Disponíveis */}
        <Slide direction="up" in={true} timeout={600}>
          <Card sx={{ 
            mb: 4,
            background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(theme.palette.background.paper, 0.98)} 100%)`,
            backdropFilter: 'blur(10px)',
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2} mb={3}>
                <Box display="flex" alignItems="center">
                  <Badge badgeContent={pedidosDisponiveis.length} color="primary" sx={{ mr: 2 }}>
                    <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 48, height: 48 }}>
                      <DeliveryDining />
                    </Avatar>
                  </Badge>
                  <Box>
                    <Typography variant="h5" fontWeight="bold" color="primary">
                      Pedidos Disponíveis
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {pedidosDisponiveis.length} pedidos aguardando entrega
                    </Typography>
                  </Box>
                </Box>
                
                {/* Filtros por Dia */}
                <Box display="flex" gap={1} alignItems="center">
                  <Typography variant="body2" color="textSecondary">Filtrar:</Typography>
                  <Button
                    variant={dayFilter === 'todos' ? 'contained' : 'outlined'}
                    size="small"
                    onClick={() => setDayFilter('todos')}
                  >
                    Todos
                  </Button>
                  <Button
                    variant={dayFilter === 'hoje' ? 'contained' : 'outlined'}
                    size="small"
                    onClick={() => setDayFilter('hoje')}
                  >
                    Hoje
                  </Button>
                  <Button
                    variant={dayFilter === 'ontem' ? 'contained' : 'outlined'}
                    size="small"
                    onClick={() => setDayFilter('ontem')}
                  >
                    Ontem
                  </Button>
                </Box>
                {pedidosDisponiveis.length > 0 && (
                  <Chip 
                    icon={<NotificationsActive />}
                    label={`${pedidosDisponiveis.length} novos`}
                    color="primary"
                    variant="outlined"
                    sx={{ 
                      animation: 'pulse 2s infinite',
                      '@keyframes pulse': {
                        '0%': { transform: 'scale(1)' },
                        '50%': { transform: 'scale(1.05)' },
                        '100%': { transform: 'scale(1)' }
                      }
                    }}
                  />
                )}
              </Box>
              
              {pedidosDisponiveis.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <Avatar sx={{ 
                    bgcolor: alpha(theme.palette.info.main, 0.1), 
                    color: theme.palette.info.main,
                    width: 80,
                    height: 80,
                    mx: 'auto',
                    mb: 2
                  }}>
                    <LocalOffer sx={{ fontSize: 40 }} />
                  </Avatar>
                  <Typography variant="h6" color="textSecondary" mb={1}>
                    Nenhum pedido disponível
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Fique de olho! Novos pedidos podem aparecer a qualquer momento
                  </Typography>
                </Box>
              ) : (
                <>
                <Box sx={{ display: { xs: 'grid', md: 'none' }, gap: 2 }}>
                  {pedidosDisponiveis.map((pedido) => (
                    <Paper key={`${pedido.entity_type || 'order'}-${pedido.id}`} variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1} mb={1}>
                        <Box>
                          <Typography fontWeight="bold" color="primary">{pedido.entity_type === 'delivery_call' ? 'Corrida avulsa' : 'Pedido'} #{pedido.id}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            {new Date(pedido.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                          </Typography>
                        </Box>
                        <Chip label={getStatusText(pedido.status)} color={getStatusColor(pedido.status)} size="small" />
                      </Box>
                      <Typography fontWeight="medium">{pedido.customer_name}</Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5, wordBreak: 'break-word' }}>
                        {pedido.delivery_address}
                      </Typography>
                      {pedido.reference && <Typography variant="body2" color="primary" sx={{ mt: 0.5 }}>Ref: {pedido.reference}</Typography>}
                      <Box display="flex" justifyContent="space-between" alignItems="center" mt={2} gap={1}>
                        <Typography fontWeight="bold" color="primary">R$ {parseFloat(pedido.total_amount || 0).toFixed(2)}</Typography>
                        <Typography variant="body2" color="textSecondary">{pedido.customer_phone}</Typography>
                      </Box>
                      <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        startIcon={<MotorcycleIcon />}
                        onClick={() => handleAceitarPedido(pedido)}
                        sx={{ mt: 2, minHeight: 48, borderRadius: 2 }}
                      >
                        Aceitar pedido
                      </Button>
                    </Paper>
                  ))}
                </Box>
                <TableContainer component={Paper} sx={{ 
                  display: { xs: 'none', md: 'block' },
                  borderRadius: 3, 
                  overflow: 'hidden',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
                }}>
                  <Table>
                    <TableHead sx={{ 
                      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`
                    }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                          <Box display="flex" alignItems="center">
                            <LocalOffer sx={{ mr: 1, fontSize: 18 }} />
                            Pedido
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                          <Box display="flex" alignItems="center">
                            <Person sx={{ mr: 1, fontSize: 18 }} />
                            Cliente
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                          <Box display="flex" alignItems="center">
                            <Map sx={{ mr: 1, fontSize: 18 }} />
                            Endereço
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                          <Box display="flex" alignItems="center">
                            <LocalOffer sx={{ mr: 1, fontSize: 18 }} />
                            Total
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', color: theme.palette.primary.main }} align="right">Ações</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pedidosDisponiveis.map((pedido, index) => (
                        <Fade in={true} timeout={800 + (index * 100)} key={`${pedido.entity_type || 'order'}-${pedido.id}`}>
                          <TableRow sx={{ 
                            '&:hover': { 
                              bgcolor: alpha(theme.palette.primary.main, 0.04),
                              transform: 'scale(1.01)',
                              transition: 'all 0.3s ease'
                            },
                            transition: 'all 0.3s ease'
                          }}>
                            <TableCell>
                              <Box>
                                <Typography fontWeight="bold" color="primary" variant="body2">
                                  #{pedido.id}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  {new Date(pedido.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box>
                                <Box display="flex" alignItems="center" mb={0.5}>
                                  <Phone fontSize="small" sx={{ mr: 1, color: theme.palette.text.secondary }} />
                                  <Typography fontWeight="medium">{pedido.customer_name}</Typography>
                                </Box>
                                <Typography variant="caption" color="textSecondary">
                                  {pedido.customer_phone}
                                </Typography>
                                {pedido.reference && (
                                  <Typography variant="caption" color="primary" sx={{ fontWeight: 'medium', mt: 0.5, display: 'block' }}>
                                    Ref: {pedido.reference}
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box display="flex" alignItems="center">
                                <MapIcon fontSize="small" sx={{ mr: 1, color: theme.palette.text.secondary }} />
                                <Typography variant="body2" sx={{ maxWidth: 200 }}>
                                  {pedido.delivery_address}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography fontWeight="bold" color="primary" variant="body2">
                                R$ {parseFloat(pedido.total_amount || 0).toFixed(2)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={getStatusText(pedido.status)}
                                color={getStatusColor(pedido.status)}
                                size="small"
                                sx={{ 
                                  fontWeight: 'bold',
                                  animation: pedido.status === 'pendente' ? 'pulse 2s infinite' : 'none'
                                }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Button
                                variant="contained"
                                color="primary"
                                startIcon={<MotorcycleIcon />}
                                onClick={() => handleAceitarPedido(pedido)}
                                sx={{ 
                                  borderRadius: 3,
                                  px: 3,
                                  py: 1,
                                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                  '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 6px 20px rgba(0,0,0,0.25)'
                                  }
                                }}
                              >
                                Aceitar
                              </Button>
                            </TableCell>
                          </TableRow>
                        </Fade>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                </>
              )}
            </CardContent>
          </Card>
        </Slide>

        {/* Meus Pedidos */}
        <Slide direction="up" in={true} timeout={800}>
          <Card sx={{ 
            background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(theme.palette.background.paper, 0.98)} 100%)`,
            backdropFilter: 'blur(10px)',
            border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                <Box display="flex" alignItems="center">
                  <Badge badgeContent={meusPedidos.length} color="success" sx={{ mr: 2 }}>
                    <Avatar sx={{ bgcolor: theme.palette.success.main, width: 48, height: 48 }}>
                      <AssignmentTurnedIn />
                    </Avatar>
                  </Badge>
                  <Box>
                    <Typography variant="h5" fontWeight="bold" color="success.main">
                      Minhas Entregas
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {meusPedidos.length} entregas em andamento
                    </Typography>
                  </Box>
                </Box>
                {meusPedidos.length > 0 && (
                  <Chip 
                    icon={<DirectionsBike />}
                    label="Em andamento"
                    color="success"
                    variant="outlined"
                  />
                )}
              </Box>
              
              {meusPedidos.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <Avatar sx={{ 
                    bgcolor: alpha(theme.palette.info.main, 0.1), 
                    color: theme.palette.info.main,
                    width: 80,
                    height: 80,
                    mx: 'auto',
                    mb: 2
                  }}>
                    <DirectionsBike sx={{ fontSize: 40 }} />
                  </Avatar>
                  <Typography variant="h6" color="textSecondary" mb={1}>
                    Nenhuma entrega ativa
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Aceite pedidos disponíveis para começar suas entregas
                  </Typography>
                </Box>
              ) : (
                <TableContainer component={Paper} sx={{ 
                  borderRadius: 3, 
                  overflow: 'hidden',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
                }}>
                  <Table>
                    <TableHead sx={{ 
                      background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.05)} 0%, ${alpha(theme.palette.success.main, 0.02)} 100%)`
                    }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', color: theme.palette.success.main }}>
                          <Box display="flex" alignItems="center">
                            <LocalOffer sx={{ mr: 1, fontSize: 18 }} />
                            Pedido
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold', color: theme.palette.success.main }}>
                          <Box display="flex" alignItems="center">
                            <Person sx={{ mr: 1, fontSize: 18 }} />
                            Cliente
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold', color: theme.palette.success.main }}>
                          <Box display="flex" alignItems="center">
                            <Map sx={{ mr: 1, fontSize: 18 }} />
                            Endereço
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold', color: theme.palette.success.main }}>
                          <Box display="flex" alignItems="center">
                            <LocalOffer sx={{ mr: 1, fontSize: 18 }} />
                            Total
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold', color: theme.palette.success.main }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', color: theme.palette.success.main }} align="right">Ações</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {meusPedidos.map((pedido, index) => (
                        <Fade in={true} timeout={1000 + (index * 100)} key={`${pedido.entity_type || 'order'}-${pedido.id}`}>
                          <TableRow sx={{ 
                            '&:hover': { 
                              bgcolor: alpha(theme.palette.success.main, 0.04),
                              transform: 'scale(1.01)',
                              transition: 'all 0.3s ease'
                            },
                            transition: 'all 0.3s ease'
                          }}>
                            <TableCell>
                              <Box>
                                <Typography fontWeight="bold" color="success.main" variant="body2">
                                  #{pedido.id}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  {new Date(pedido.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box>
                                <Box display="flex" alignItems="center" mb={0.5}>
                                  <Phone fontSize="small" sx={{ mr: 1, color: theme.palette.text.secondary }} />
                                  <Typography fontWeight="medium">{pedido.customer_name}</Typography>
                                </Box>
                                <Typography variant="caption" color="textSecondary">
                                  {pedido.customer_phone}
                                </Typography>
                                {pedido.reference && (
                                  <Typography variant="caption" color="primary" sx={{ fontWeight: 'medium', mt: 0.5, display: 'block' }}>
                                    Ref: {pedido.reference}
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box display="flex" alignItems="center">
                                <MapIcon fontSize="small" sx={{ mr: 1, color: theme.palette.text.secondary }} />
                                <Typography variant="body2" sx={{ maxWidth: 200 }}>
                                  {pedido.delivery_address}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography fontWeight="bold" color="success.main" variant="body2">
                                R$ {parseFloat(pedido.total_amount || 0).toFixed(2)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={getStatusText(pedido.status)}
                                color={getStatusColor(pedido.status)}
                                size="small"
                                sx={{ 
                                  fontWeight: 'bold',
                                  animation: pedido.status === 'saiu_entrega' ? 'pulse 2s infinite' : 'none'
                                }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              {pedido.status === 'aceito' && (
                                <Button
                                  variant="contained"
                                  color="primary"
                                  startIcon={<MotorcycleIcon />}
                                  onClick={() => handleStatusEntrega(pedido, 'saiu_entrega')}
                                  sx={{ 
                                    borderRadius: 3,
                                    px: 2,
                                    py: 1,
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                    '&:hover': {
                                      transform: 'translateY(-2px)',
                                      boxShadow: '0 6px 20px rgba(0,0,0,0.25)'
                                    }
                                  }}
                                >
                                  Saiu para Entrega
                                </Button>
                              )}
                              {pedido.status === 'saiu_entrega' && (
                                <Button
                                  variant="contained"
                                  color="success"
                                  startIcon={<CheckCircle />}
                                  onClick={() => handleStatusEntrega(pedido, 'entregue')}
                                  sx={{ 
                                    borderRadius: 3,
                                    px: 3,
                                    py: 1,
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                    '&:hover': {
                                      transform: 'translateY(-2px)',
                                      boxShadow: '0 6px 20px rgba(0,0,0,0.25)'
                                    }
                                  }}
                                >
                                  Entregue
                                </Button>
                              )}
                              {pedido.entity_type === 'delivery_call' && pedido.status === 'delivering' && (
                                <Button variant="contained" color="success" startIcon={<CheckCircle />}
                                  onClick={() => handleStatusEntrega(pedido, 'entregue')}>
                                  Entregue
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        </Fade>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Slide>
      </Box>

      <Dialog open={pixDialogOpen} onClose={() => !payoutBusy && setPixDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Chave Pix para receber</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            A chave fica protegida no servidor e só é usada para processar seu saque.
          </Typography>
          <FormControl fullWidth margin="dense">
            <InputLabel id="motoboy-pix-type-label">Tipo de chave</InputLabel>
            <Select
              labelId="motoboy-pix-type-label"
              value={pixKeyType}
              label="Tipo de chave"
              onChange={(event) => setPixKeyType(event.target.value)}
            >
              <MenuItem value="CPF">CPF</MenuItem>
              <MenuItem value="CNPJ">CNPJ</MenuItem>
              <MenuItem value="EMAIL">E-mail</MenuItem>
              <MenuItem value="PHONE">Celular</MenuItem>
              <MenuItem value="EVP">Chave aleatória</MenuItem>
            </Select>
          </FormControl>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Chave Pix"
            value={pixKeyValue}
            onChange={(event) => setPixKeyValue(event.target.value)}
            autoComplete="off"
            inputProps={{ maxLength: 140 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPixDialogOpen(false)} disabled={payoutBusy}>Cancelar</Button>
          <Button onClick={handleSavePixKey} variant="contained" disabled={payoutBusy || !pixKeyValue.trim()}>
            {payoutBusy ? 'Salvando...' : 'Salvar chave Pix'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de detalhes do pedido buscado */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: 'hidden',
            boxShadow: '0 20px 40px rgba(0,0,0,0.15)'
          }
        }}
      >
        <DialogTitle sx={{ 
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: theme.palette.primary.contrastText,
          py: 3
        }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              <Avatar sx={{ bgcolor: alpha('#fff', 0.2), mr: 2 }}>
                <LocalOffer />
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  Pedido #{pedidoBuscado?.id}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Detalhes completos da entrega
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={handleCloseDialog} sx={{ color: 'inherit' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 4, bgcolor: alpha(theme.palette.background.paper, 0.98) }}>
          {pedidoBuscado && (
            <Box>
              {/* Informações do Cliente */}
              <Box mb={4}>
                <Box display="flex" alignItems="center" mb={2}>
                  <Avatar sx={{ bgcolor: theme.palette.primary.main, mr: 2 }}>
                    <Person />
                  </Avatar>
                  <Typography variant="h6" fontWeight="bold" color="primary">
                    Informações do Cliente
                  </Typography>
                </Box>
                <Card sx={{ 
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Box display="flex" alignItems="center" mb={1}>
                          <Person fontSize="small" sx={{ mr: 1, color: theme.palette.text.secondary }} />
                          <Typography variant="body2" color="textSecondary">
                            Nome do Cliente
                          </Typography>
                        </Box>
                        <Typography variant="body1" fontWeight="bold">
                          {pedidoBuscado.customer_name}
                        </Typography>
                        {pedidoBuscado.reference && (
                          <Typography variant="body2" color="primary" sx={{ fontWeight: 'medium', mt: 1 }}>
                            Ref: {pedidoBuscado.reference}
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Box display="flex" alignItems="center" mb={1}>
                          <Phone fontSize="small" sx={{ mr: 1, color: theme.palette.text.secondary }} />
                          <Typography variant="body2" color="textSecondary">
                            Telefone
                          </Typography>
                        </Box>
                        <Typography variant="body1" fontWeight="bold">
                          {pedidoBuscado.customer_phone}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Box display="flex" alignItems="center" mb={1}>
                          <MapIcon fontSize="small" sx={{ mr: 1, color: theme.palette.text.secondary }} />
                          <Typography variant="body2" color="textSecondary">
                            Endereço de Entrega
                          </Typography>
                        </Box>
                        <Typography variant="body1" fontWeight="bold">
                          {pedidoBuscado.delivery_address}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Box display="flex" alignItems="center" mb={1}>
                          <LocalOffer fontSize="small" sx={{ mr: 1, color: theme.palette.text.secondary }} />
                          <Typography variant="body2" color="textSecondary">
                            Referência
                          </Typography>
                        </Box>
                        <Typography variant="body1" fontWeight="bold">
                          {pedidoBuscado.reference || 'Não informada'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Box>

              {/* Informações do Pedido */}
              <Box mb={4}>
                <Box display="flex" alignItems="center" mb={2}>
                  <Avatar sx={{ bgcolor: theme.palette.success.main, mr: 2 }}>
                    <LocalShipping />
                  </Avatar>
                  <Typography variant="h6" fontWeight="bold" color="success.main">
                    Informações do Pedido
                  </Typography>
                </Box>
                <Card sx={{ 
                  background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.02)} 0%, ${alpha(theme.palette.success.main, 0.05)} 100%)`,
                  border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={4}>
                        <Box display="flex" alignItems="center" mb={1}>
                          <AccessTime fontSize="small" sx={{ mr: 1, color: theme.palette.text.secondary }} />
                          <Typography variant="body2" color="textSecondary">
                            Status
                          </Typography>
                        </Box>
                        <Chip
                          label={getStatusText(pedidoBuscado.status)}
                          color={getStatusColor(pedidoBuscado.status)}
                          size="medium"
                          sx={{ fontWeight: 'bold' }}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Box display="flex" alignItems="center" mb={1}>
                          <LocalOffer fontSize="small" sx={{ mr: 1, color: theme.palette.text.secondary }} />
                          <Typography variant="body2" color="textSecondary">
                            Total
                          </Typography>
                        </Box>
                        <Typography variant="h5" fontWeight="bold" color="primary">
                          R$ {parseFloat(pedidoBuscado.total_amount || 0).toFixed(2)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Box display="flex" alignItems="center" mb={1}>
                          <AccessTime fontSize="small" sx={{ mr: 1, color: theme.palette.text.secondary }} />
                          <Typography variant="body2" color="textSecondary">
                            Data do Pedido
                          </Typography>
                        </Box>
                        <Typography variant="body1">
                          {new Date(pedidoBuscado.created_at).toLocaleString('pt-BR')}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Box>

              {/* Itens do Pedido */}
              <Box mb={4}>
                <Box display="flex" alignItems="center" mb={2}>
                  <Avatar sx={{ bgcolor: theme.palette.secondary.main, mr: 2 }}>
                    <ShoppingCart />
                  </Avatar>
                  <Typography variant="h6" fontWeight="bold" color="secondary.main">
                    Itens do Pedido
                  </Typography>
                </Box>
                <TableContainer component={Paper} sx={{ 
                  borderRadius: 3, 
                  overflow: 'hidden',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
                }}>
                  <Table>
                    <TableHead sx={{ 
                      background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`
                    }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', color: theme.palette.secondary.main }}>
                          <Box display="flex" alignItems="center">
                            <ShoppingCart sx={{ mr: 1, fontSize: 18 }} />
                            Produto
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold', color: theme.palette.secondary.main }}>Quantidade</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', color: theme.palette.secondary.main }}>Preço Unit.</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', color: theme.palette.secondary.main }} align="right">Subtotal</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pedidoBuscado.items?.map((item, index) => (
                        <Fade in={true} timeout={1200 + (index * 100)} key={index}>
                          <TableRow sx={{ 
                            '&:hover': { bgcolor: alpha(theme.palette.action.hover, 0.04) },
                            transition: 'all 0.3s ease'
                          }}>
                            <TableCell>
                              <Typography fontWeight="medium">
                                {item.product_name || item.produto_name}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={item.quantity} 
                                size="small" 
                                color="primary" 
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                R$ {parseFloat(item.unit_price || 0).toFixed(2)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body1" fontWeight="bold" color="primary">
                                R$ {parseFloat((item.quantity || 0) * (item.unit_price || 0)).toFixed(2)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        </Fade>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {/* Ações se for o motoboy responsável */}
              {pedidoBuscado.motoboy_id === user?.id && (
                <Box>
                  <Divider sx={{ mb: 3 }} />
                  <Box display="flex" alignItems="center" mb={3}>
                    <Avatar sx={{ bgcolor: theme.palette.warning.main, mr: 2 }}>
                      <CheckCircle />
                    </Avatar>
                    <Typography variant="h6" fontWeight="bold" color="warning.main">
                      Ações do Pedido
                    </Typography>
                  </Box>
                  <Box display="flex" gap={2} flexWrap="wrap">
                    {pedidoBuscado.status === 'aceito' && (
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<MotorcycleIcon />}
                        onClick={() => handleStatusEntrega(pedidoBuscado.id, 'saiu_entrega')}
                        sx={{ 
                          borderRadius: 3,
                          px: 4,
                          py: 1.5,
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 6px 20px rgba(0,0,0,0.25)'
                          }
                        }}
                      >
                        Saiu para Entrega
                      </Button>
                    )}
                    {pedidoBuscado.status === 'saiu_entrega' && (
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<CheckCircle />}
                        onClick={() => handleStatusEntrega(pedidoBuscado.id, 'entregue')}
                        sx={{ 
                          borderRadius: 3,
                          px: 4,
                          py: 1.5,
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 6px 20px rgba(0,0,0,0.25)'
                          }
                        }}
                      >
                        Entregue
                      </Button>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          p: 3, 
          bgcolor: alpha(theme.palette.background.paper, 0.98),
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`
        }}>
          <Button 
            onClick={handleCloseDialog} 
            variant="contained" 
            sx={{ 
              borderRadius: 3,
              px: 4
            }}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default MotoboyDashboard;
